import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Trophy, Star, Award, Users } from "lucide-react";

function resolvePhoto(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${API.replace(/\/api$/, "")}${url}`;
}

export default function GauravLeaderboard() {
  const [top, setTop] = useState([]);
  const [ref, setRef] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/contributor/top-contributors").then((r) => setTop(r.data)).catch(() => {}),
      api.get("/contributor/leaderboard").then((r) => setRef(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading || (top.length === 0 && ref.length === 0)) return null;

  return (
    <section className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="gaurav-leaderboard">
      {top.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 border-2 border-amber-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-amber-600"/>
            <h3 className="font-devnagri-heading text-lg font-black text-[#0F172A]">शीर्ष योगदानकर्ता</h3>
            <div className="h-px bg-amber-200 flex-1"></div>
            <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Top 5</span>
          </div>
          <div className="space-y-2">
            {top.slice(0, 5).map((c, i) => (
              <div key={`${c.name}-${i}`} className="flex items-center gap-3 bg-white/70 backdrop-blur rounded-lg p-2.5 border border-amber-100" data-testid={`top-contrib-${i}`}>
                <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-black text-xs shadow ${
                  i === 0 ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white" :
                  i === 1 ? "bg-gradient-to-br from-slate-300 to-slate-500 text-white" :
                  i === 2 ? "bg-gradient-to-br from-orange-400 to-orange-700 text-white" :
                  "bg-white border border-slate-200 text-slate-500"
                }`}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}</div>
                {c.photo_url ? (
                  <img src={resolvePhoto(c.photo_url)} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-amber-200"/>
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-black text-xs">
                    {c.name?.[0] || "?"}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm text-[#0F172A] truncate">{c.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{c.city || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">रिपोर्ट्स</div>
                  <div className="text-lg font-black text-[#EA580C] leading-none">{c.count}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {ref.length > 0 && (
        <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-50 border-2 border-emerald-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-emerald-600"/>
            <h3 className="font-devnagri-heading text-lg font-black text-[#0F172A]">गौरव पाठक</h3>
            <div className="h-px bg-emerald-200 flex-1"></div>
            <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1"><Users className="w-3 h-3"/>दोस्त लाए</span>
          </div>
          <div className="space-y-2">
            {ref.slice(0, 5).map((c, i) => (
              <div key={`${c.name}-${i}`} className="flex items-center gap-3 bg-white/70 backdrop-blur rounded-lg p-2.5 border border-emerald-100" data-testid={`ref-lead-${i}`}>
                {c.photo_url ? (
                  <img src={resolvePhoto(c.photo_url)} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-emerald-200"/>
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 text-white flex items-center justify-center font-black text-xs">
                    {c.name?.[0] || "?"}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm text-[#0F172A] truncate flex items-center gap-1.5">
                    {c.name}
                    {c.badge && (
                      <span className="inline-flex items-center gap-0.5 text-[8px] font-black bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-1.5 py-0.5 rounded-full uppercase tracking-widest">
                        <Star className="w-2 h-2 fill-current"/> {c.badge}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">{c.city || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">रेफर</div>
                  <div className="text-lg font-black text-emerald-600 leading-none">+{c.referred}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-center text-slate-500 mt-3 pt-3 border-t border-emerald-100">
            💡 अपनी रिपोर्ट सबमिट करके 3 दोस्तों को बुलाइए — <b className="text-emerald-700">"गौरव पाठक"</b> बैज पाइये
          </p>
        </div>
      )}
    </section>
  );
}
